import React, { useState, useEffect } from 'react';
import {
    Dialog,
    Button,
    Box
} from '@mui/material';
import { MdClose } from 'react-icons/md';
import useRoleApi from '../../api/useRoleApi';
import useAdminsApi from '../../api/useAdminsApi';

const AddAdminDialog = ({ open, onClose, handleAddAdmin }) => {
    const { loading: adminLoading } = useAdminsApi();

    const [formData, setFormData] = useState({
        email: '',
        role_id: '',
        user_name: '',
        phone_number: ''
    });
    const [roles, setRoles] = useState([]);
    const [loadingRoles, setLoadingRoles] = useState(false);
    const [error, setError] = useState('');
    const { getRoles, loading } = useRoleApi();

    useEffect(() => {
        if (open) {
            fetchRoles();
        }
    }, [open]);

    const fetchRoles = async () => {
        setLoadingRoles(true);
        setError('');
        try {
            const response = await getRoles(true);
            const activeRoles = (response.data?.data || []).filter(r => r.is_live);
            setRoles(activeRoles || []);
        } catch (err) {
            console.error(err);
            setError('Failed to load roles.');
        } finally {
            setLoadingRoles(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'phone_number') {
            // Only allow numbers and limit to 10 digits
            const numbersOnly = value.replace(/[^0-9]/g, '').slice(0, 10);
            setFormData(prev => ({ ...prev, [name]: numbersOnly }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleReset = () => {
        setFormData({
            email: '',
            role_id: '',
            user_name: '',
            phone_number: ''
        });
        onClose();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        handleAddAdmin(e, formData);
    };

    return (
        <Dialog
            open={open}
            onClose={handleReset}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: '16px' } }}
        >
            <form onSubmit={handleSubmit}>
                <div className="card mb-0">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h6 className="card-title mb-0">Add Admin</h6>
                        <button type="button" className="btn btn-sm btn-light p-1" onClick={handleReset}>
                            <MdClose size={18} />
                        </button>
                    </div>

                    <div className="card-body">
                        <div className="row gy-3">
                            <div className="col-md-6">
                                <label className="form-label">Name*</label>
                                <input
                                    type="text"
                                    className="form-control"
                                    name="user_name"
                                    placeholder="Enter Name"
                                    value={formData.user_name}
                                    onChange={handleInputChange}
                                    required
                                    minLength={3}
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Mobile Number*</label>
                                <input
                                    type="tel"
                                    className="form-control"
                                    name="phone_number"
                                    placeholder="Enter Mobile Number"
                                    value={formData.phone_number}
                                    onChange={handleInputChange}
                                    required
                                    pattern="[0-9]{10}"
                                    title="Please enter a valid 10-digit mobile number"
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Email*</label>
                                <input
                                    type="email"
                                    className="form-control"
                                    name="email"
                                    placeholder="Enter Email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="col-md-6">
                                <label className="form-label">Role*</label>
                                {loadingRoles ? (
                                    <div>Loading roles...</div>
                                ) : error ? (
                                    <div className="text-danger">{error}</div>
                                ) : (
                                    <select
                                        className="form-select"
                                        name="role_id"
                                        value={formData.role_id}
                                        onChange={handleInputChange}
                                        required
                                    >
                                        <option value="">Select Role</option>
                                        {roles?.map((r) => (
                                            <option key={r._id} value={r._id}>
                                                {r.role}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            <div className="col-12 text-end">
                                <button type="button" className="btn btn-secondary me-2" onClick={handleReset}>
                                    Cancel
                                </button>
                                <button 
                                    disabled={adminLoading}
                                    type="submit" 
                                    className="btn btn-primary"
                                >
                                    {adminLoading ? (
                                        <span className="spinner-border spinner-border-sm" />
                                    ) : (
                                        "Save"
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </form>
        </Dialog>
    );
};

export default AddAdminDialog;
